import { ensureOrgEmployees } from "../../../../../../services/useOrgData";
import React, { useState, useEffect, useMemo } from "react";
import { Box, Stack } from '@mui/material';
import { useNavigate } from "react-router";
import DataTable from "../../../../../../component/DataTable";
import MainBox from "../../../../container/MainBox";
import BtnSecondary from "../../../../../../component/SecondaryButton";
import { SearchInput, StatusChip, headCellSx, bodyCellSx } from "../../../../../../component/listing";

import { getAllSalaryStructures } from "../../../../../../services/payroll";
import { useNotify } from "../../../../../../component/NotificationProvider";
import { tokens, displayFont } from "../../../../../../theme";
import { money } from "../payrollUtils";

const SalaryStructureList = () => {
  const navigate = useNavigate();
  const notify = useNotify();
  const [employees, setEmployees] = useState([]);
  const [byEmployee, setByEmployee] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [search, setSearch] = useState("");

  const fetchData = async () => {
    setLoading(true);
    setError(false);
    try {
      const [employees, structRes] = await Promise.all([ensureOrgEmployees(), getAllSalaryStructures()]);
      setEmployees(Array.isArray(employees) ? employees : []);
      const m = {};
      (structRes?.data || []).forEach((s) => { m[s.employee?._id || s.employee] = s; });
      setByEmployee(m);
    } catch (e) {
      setError(true);
      notify.error(e?.message || "Failed to load salary structures");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, []);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return employees
      .map((e) => ({ emp: e, structure: byEmployee[e._id] }))
      .filter(({ emp }) => !q || `${emp.fname} ${emp.lname}`.toLowerCase().includes(q) || (emp.aliasId || "").toLowerCase().includes(q));
  }, [employees, byEmployee, search]);

  return (
    <Box sx={{ width: "100%" }}>
      <MainBox sx={{ padding: "1.25rem" }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: "1.25rem" }}>
          <Box sx={{ fontFamily: displayFont, fontSize: "1.25rem", fontWeight: 600, color: tokens.text }}>Salary Structures</Box>
          <SearchInput value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search employee" minWidth="16rem" />
        </Stack>

        <DataTable
          rows={rows}
          loading={loading}
          error={error}
          onRetry={fetchData}
          errorTitle="Couldn't load data"
          empty={{ title: "No employees" }}
          getRowId={({ emp }) => emp._id}
          onRowClick={({ emp }) => navigate(`/app/settings/payroll/structures/${emp._id}`)}
          tableSx={{ width: "100%", tableLayout: "fixed" }}
          columns={[
            {
              key: "employee",
              label: "Employee",
              width: "28%",
              cellSx: { fontWeight: 600 },
              render: ({ emp }) => `${emp.fname || ""} ${emp.lname || ""}`.trim(),
            },
            { key: "designation", label: "Designation", cellSx: { color: tokens.textMuted }, render: ({ emp }) => emp.designation || "—" },
            { key: "ctc", label: "CTC (annual)", align: "right", render: ({ structure }) => (structure ? money(structure.ctc) : "—") },
            { key: "net", label: "Net / month", align: "right", render: ({ structure }) => (structure ? money(structure.monthlyNet) : "—") },
            {
              key: "status",
              label: "Status",
              render: ({ structure }) => <StatusChip label={structure ? "Configured" : "Not set"} tone={structure ? "success" : "muted"} />,
            },
            {
              key: "action",
              label: "Action",
              width: "9rem",
              align: "right",
              // Opens the same place the row does, so it must not fire twice.
              render: ({ emp, structure }) => (
                <Box onClick={(e) => e.stopPropagation()}>
                  <BtnSecondary
                    onClick={() => navigate(`/app/settings/payroll/structures/${emp._id}`)}
                    sx={{ height: "2.25rem", px: "1rem", borderRadius: "8px", whiteSpace: "nowrap" }}
                  >
                    {structure ? "Edit" : "Configure"}
                  </BtnSecondary>
                </Box>
              ),
            },
          ]}
        />
      </MainBox>
    </Box>
  );
};

export default SalaryStructureList;
